// src/components/meetings/MeetingForm.js
import React, { useState, useEffect, useContext } from 'react';
import { 
  Calendar, Clock, MapPin, User, Plus, X, 
  ChevronDown, ChevronUp, ArrowLeft, Check, RotateCw, Save,
  Building, Users, AlertCircle
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { AppContext } from '../../context/AppContext';
import { 
  collection, 
  doc, 
  getDoc, 
  addDoc, 
  updateDoc, 
  serverTimestamp,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import { db } from '../../firebase/config';

const MeetingForm = ({ meeting, onSave, isEditing = false }) => {
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const { activeBusiness } = useContext(AppContext);
  
  const [formData, setFormData] = useState({
    title: '',
    type: '',
    date: '',
    startTime: '',
    endTime: '',
    location: '',
    chairperson: '',
    description: '',
    participants: [],
    department: '',
    priority: 'medium'
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showParticipantSearch, setShowParticipantSearch] = useState(false);
  const [success, setSuccess] = useState('');
  const [employees, setEmployees] = useState([]);
  const { meetingId } = useParams();
  
  // Business meeting types
  const meetingTypes = [
    'Board Meeting',
    'Team Meeting', 
    'Department Meeting',
    'All Hands',
    'Project Review',
    'Budget Review',
    'Strategy Session',
    'Client Meeting',
    'Vendor Meeting',
    'Training Session',
    'Performance Review',
    'Planning Session',
    'Other'
  ];

  // Priority levels
  const priorityLevels = [
    { value: 'low', label: 'Low Priority', color: 'text-green-600' },
    { value: 'medium', label: 'Medium Priority', color: 'text-yellow-600' },
    { value: 'high', label: 'High Priority', color: 'text-red-600' },
    { value: 'urgent', label: 'Urgent', color: 'text-purple-600' }
  ];

  // Fetch employees for participant selection
  useEffect(() => {
    const fetchEmployees = async () => {
      if (!activeBusiness?.id) return;
      
      try {
        const employeesRef = collection(db, "employees");
        const q = query(employeesRef, where("businessId", "==", activeBusiness.id));
        const querySnapshot = await getDocs(q);
        
        const employeesList = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          employeesList.push({
            id: doc.id,
            name: `${data.firstName} ${data.lastName}`,
            department: data.department,
            position: data.position,
            email: data.email
          });
        });
        
        setEmployees(employeesList);
      } catch (err) {
        console.error("Error fetching employees:", err);
      }
    };
    
    fetchEmployees();
  }, [activeBusiness]);

  // Initialize form data from meeting prop if editing
  useEffect(() => {
    if (meeting) {
      const date = meeting.date ? new Date(meeting.date) : new Date();
      const endTime = meeting.endTime ? new Date(meeting.endTime) : new Date(date.getTime() + 60 * 60 * 1000);
      
      setFormData({
        title: meeting.title || '',
        type: meeting.type || '',
        date: date.toISOString().split('T')[0],
        startTime: date.toTimeString().slice(0, 5),
        endTime: endTime.toTimeString().slice(0, 5),
        location: meeting.location || '',
        chairperson: meeting.chairperson || '',
        description: meeting.description || '',
        participants: meeting.participants || [],
        department: meeting.department || '',
        priority: meeting.priority || 'medium'
      });
    }
  }, [meeting]);

  useEffect(() => {
    if (isEditing && meetingId) {
      fetchMeeting();
    }
  }, [isEditing, meetingId]);
  
  const fetchMeeting = async () => {
    try {
      setIsLoading(true);
      const meetingDoc = await getDoc(doc(db, "meetings", meetingId));
      
      if (meetingDoc.exists()) {
        const meetingData = meetingDoc.data();
        
        // Convert Firestore timestamps to Date objects
        const date = meetingData.date?.toDate();
        const endTime = meetingData.endTime?.toDate();
        
        // Format date for the date input (YYYY-MM-DD)
        const formattedDate = date ? date.toISOString().split('T')[0] : '';
        
        // Format times for the time inputs (HH:MM)
        const formattedStartTime = date ? 
          `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}` : '';
        const formattedEndTime = endTime ? 
          `${endTime.getHours().toString().padStart(2, '0')}:${endTime.getMinutes().toString().padStart(2, '0')}` : '';
        
        setFormData({
          title: meetingData.title || '',
          type: meetingData.type || '',
          date: formattedDate,
          startTime: formattedStartTime,
          endTime: formattedEndTime,
          location: meetingData.location || '',
          chairperson: meetingData.chairperson || '',
          description: meetingData.description || '',
          participants: meetingData.participants || [],
          department: meetingData.department || '',
          priority: meetingData.priority || 'medium'
        });
      }
    } catch (err) {
      console.error('Error fetching meeting:', err);
      setError('Failed to load meeting data');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Search employees for participants
  const searchEmployees = (term) => {
    if (!term.trim()) {
      setSearchResults([]);
      return;
    }
    
    const filtered = employees.filter(employee =>
      employee.name.toLowerCase().includes(term.toLowerCase()) ||
      employee.department?.toLowerCase().includes(term.toLowerCase()) ||
      employee.position?.toLowerCase().includes(term.toLowerCase())
    );
    
    setSearchResults(filtered);
  };

  // Add participant to meeting
  const addParticipant = (employee) => {
    if (!formData.participants.find(p => p.id === employee.id)) {
      setFormData(prev => ({
        ...prev,
        participants: [...prev.participants, employee]
      }));
    }
    setSearchTerm('');
    setSearchResults([]);
    setShowParticipantSearch(false);
  };

  // Remove participant from meeting
  const removeParticipant = (participantId) => {
    setFormData(prev => ({
      ...prev,
      participants: prev.participants.filter(p => p.id !== participantId)
    }));
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!activeBusiness?.id) {
      setError('No active business selected');
      return;
    }
    
    setIsLoading(true);
    setError('');
    setSuccess('');
    
    try {
      // Validate required fields
      if (!formData.title.trim() || !formData.date || !formData.startTime) {
        setError('Please fill in all required fields');
        setIsLoading(false);
        return;
      }
      
      // Create datetime objects
      const dateStr = formData.date;
      const startTimeStr = formData.startTime;
      const endTimeStr = formData.endTime;
      
      const startDateTime = new Date(`${dateStr}T${startTimeStr}`);
      const endDateTime = endTimeStr ? 
        new Date(`${dateStr}T${endTimeStr}`) :
        new Date(startDateTime.getTime() + 60 * 60 * 1000); // Default to 1 hour later
      
      // Prepare meeting data
      const meetingData = {
        ...formData,
        date: startDateTime,
        endTime: endDateTime,
        participants: formData.participants || [],
        status: 'scheduled',
        createdBy: userProfile?.id || currentUser?.uid || 'unknown',
        businessId: activeBusiness.id,
        businessName: activeBusiness.name,
        updatedAt: serverTimestamp()
      };
      
      delete meetingData.startTime; // Remove separate startTime field as it's now part of date
      
      // Save to Firestore based on whether we're editing or creating
      if (isEditing && meetingId) {
        // Update existing meeting
        await updateDoc(doc(db, "meetings", meetingId), {
          ...meetingData,
          updatedAt: serverTimestamp()
        });
        setSuccess('Meeting updated successfully');
      } else {
        // Create new meeting
        meetingData.createdAt = serverTimestamp();
        const docRef = await addDoc(collection(db, "meetings"), meetingData);
        console.log("Meeting created with ID: ", docRef.id);
        setSuccess('Meeting created successfully');
      }
      
      // Navigate back after a short delay
      setTimeout(() => {
        navigate('/meetings');
      }, 1500);
      
    } catch (err) {
      console.error('Error submitting form:', err);
      setError(`Failed to ${isEditing ? 'update' : 'create'} meeting: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto bg-white shadow-md rounded-lg p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/meetings')}
            className="mr-4 text-gray-500 hover:text-gray-700"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-xl font-bold text-gray-900">
            {meeting ? 'Edit Meeting' : 'Create New Meeting'}
          </h1>
        </div>
        
        {activeBusiness && (
          <div className="flex items-center text-sm text-gray-500">
            <Building className="h-4 w-4 mr-1" />
            {activeBusiness.name}
          </div>
        )}
      </div>

      {/* Status Messages */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex">
            <Check className="h-5 w-5 text-green-400" />
            <div className="ml-3">
              <p className="text-sm text-green-600">{success}</p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
              Meeting Title *
            </label>
            <input
              type="text"
              id="title"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter meeting title"
              required
            />
          </div>

          <div>
            <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
              Meeting Type
            </label>
            <select
              id="type"
              value={formData.type}
              onChange={(e) => handleInputChange('type', e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select meeting type</option>
              {meetingTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Date and Time */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">
              Date *
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              <input
                type="date"
                id="date"
                value={formData.date}
                onChange={(e) => handleInputChange('date', e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
          </div>

          <div>
            <label htmlFor="startTime" className="block text-sm font-medium text-gray-700 mb-1">
              Start Time *
            </label>
            <div className="relative">
              <Clock className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              <input
                type="time"
                id="startTime"
                value={formData.startTime}
                onChange={(e) => handleInputChange('startTime', e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
          </div>

          <div>
            <label htmlFor="endTime" className="block text-sm font-medium text-gray-700 mb-1">
              End Time
            </label>
            <div className="relative">
              <Clock className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              <input
                type="time"
                id="endTime"
                value={formData.endTime}
                onChange={(e) => handleInputChange('endTime', e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Location and Department */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
              Location
            </label>
            <div className="relative">
              <MapPin className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              <input
                type="text"
                id="location"
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Meeting location or video link"
              />
            </div>
          </div>

          <div>
            <label htmlFor="department" className="block text-sm font-medium text-gray-700 mb-1">
              Department
            </label>
            <input
              type="text"
              id="department"
              value={formData.department}
              onChange={(e) => handleInputChange('department', e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Related department"
            />
          </div>
        </div>

        {/* Priority and Chairperson */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-1">
              Priority Level
            </label>
            <select
              id="priority"
              value={formData.priority}
              onChange={(e) => handleInputChange('priority', e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              {priorityLevels.map(level => (
                <option key={level.value} value={level.value}>{level.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="chairperson" className="block text-sm font-medium text-gray-700 mb-1">
              Meeting Chair
            </label>
            <div className="relative">
              <User className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              <input
                type="text"
                id="chairperson"
                value={formData.chairperson}
                onChange={(e) => handleInputChange('chairperson', e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Person chairing the meeting"
              />
            </div>
          </div>
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            id="description"
            rows={3}
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            placeholder="Meeting description or objectives"
          />
        </div>

        {/* Participants */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            <Users className="inline h-4 w-4 mr-1" />
            Participants
          </label>

          {/* Add Participant Button */}
          <div className="mb-4">
            <button
              type="button"
              onClick={() => setShowParticipantSearch(!showParticipantSearch)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-gray-50 hover:bg-gray-100"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Participant
            </button>
          </div>

          {/* Participant Search */}
          {showParticipantSearch && (
            <div className="mb-4 border border-gray-200 rounded-md p-4 bg-gray-50">
              <input
                type="text"
                placeholder="Search employees..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  searchEmployees(e.target.value);
                }}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 mb-2"
              />

              {searchResults.length > 0 && (
                <div className="max-h-48 overflow-y-auto">
                  {searchResults.map(employee => (
                    <div
                      key={employee.id}
                      onClick={() => addParticipant(employee)}
                      className="flex items-center justify-between p-2 hover:bg-gray-100 cursor-pointer rounded"
                    >
                      <div>
                        <div className="font-medium text-sm">{employee.name}</div>
                        <div className="text-xs text-gray-500">{employee.position} • {employee.department}</div>
                      </div>
                      <Plus className="h-4 w-4 text-gray-400" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Selected Participants */}
          {formData.participants.length > 0 && (
            <div className="border border-gray-200 rounded-md">
              {formData.participants.map((participant, index) => (
                <div key={participant.id} className="flex items-center justify-between p-3 border-b border-gray-200 last:border-b-0">
                  <div className="flex-1">
                    <div className="font-medium text-sm">{participant.name}</div>
                    {participant.department && (
                      <div className="text-xs text-gray-500">{participant.position} • {participant.department}</div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeParticipant(participant.id)}
                    className="ml-3 text-red-500 hover:text-red-700"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={() => navigate('/meetings')}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#190192] hover:bg-blue-700 disabled:opacity-50"
          >
            {isLoading && <RotateCw className="animate-spin h-4 w-4 mr-2" />}
            <Save className="h-4 w-4 mr-2" />
            {isEditing ? 'Update Meeting' : 'Create Meeting'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default MeetingForm;